import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import AuthHashRedirect from "./components/AuthHashRedirect";

// Lazy load all pages
const Index = lazy(() => import("./pages/Index"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
const QuoteSuccess = lazy(() => import("./pages/QuoteSuccess"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrimeWaterPumpGuide = lazy(() => import("./pages/PrimeWaterPumpGuide"));
const HowToTellWaterPumpIsBadGuide = lazy(() => import("./pages/HowToTellWaterPumpIsBadGuide"));
const CategoryLanding = lazy(() => import("./pages/CategoryLanding"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminForgotPassword = lazy(() => import("./pages/admin/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword"));
const AdminAcceptInvitation = lazy(() => import("./pages/admin/AdminAcceptInvitation"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory"));
const AdminAddProduct = lazy(() => import("./pages/admin/AdminAddProduct"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminDevControls = lazy(() => import("./pages/admin/AdminDevControls"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const PublicLayout = ({ children }: { children: React.ReactNode }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <LanguageProvider>
        <CartProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <AuthHashRedirect />
            <Suspense fallback={<div className="min-h-screen" />}>
              <Routes>
                <Route path="/" element={<PublicLayout><Index /></PublicLayout>} />
                <Route path="/shop" element={<PublicLayout><Shop /></PublicLayout>} />
                <Route path="/shop/:slug" element={<PublicLayout><CategoryLanding /></PublicLayout>} />
                <Route path="/product/:id" element={<PublicLayout><ProductDetail /></PublicLayout>} />
                <Route path="/cart" element={<PublicLayout><Cart /></PublicLayout>} />
                <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
                <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                <Route path="/payment-return" element={<PublicLayout><PaymentReturn /></PublicLayout>} />
                <Route path="/quote-success" element={<PublicLayout><QuoteSuccess /></PublicLayout>} />
                <Route path="/track-order" element={<PublicLayout><TrackOrder /></PublicLayout>} />
                <Route path="/unsubscribe" element={<PublicLayout><Unsubscribe /></PublicLayout>} />
                <Route path="/guides/how-to-prime-water-pump" element={<PublicLayout><PrimeWaterPumpGuide /></PublicLayout>} />
                <Route path="/guides/how-to-tell-if-water-pump-is-bad" element={<PublicLayout><HowToTellWaterPumpIsBadGuide /></PublicLayout>} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                <Route path="/admin/accept-invitation" element={<AdminAcceptInvitation />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="inventory" element={<AdminInventory />} />
                  <Route path="products/add" element={<AdminAddProduct />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="dev-controls" element={<AdminDevControls />} />
                  <Route path="audit-log" element={<AdminAuditLog />} />
                </Route>
                <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </CartProvider>
        </LanguageProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
